import { useMemo } from 'react';
import type { DatabaseMetaInfo } from '../../services/metaInfoApi';
import type { FilterGroup, TableExplorerColumn } from '../../../core/types/workspaceTypes';
import type { EnumLookupTarget } from '../../../../../types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import {
  inferEnumOperatorPlacement,
  type EnumColumnInput,
} from '../../utils/enumColumnCategorization';
import { findLooseRelationshipsForColumn } from '../../utils/looseFkResolver';
import { resolveLookupEnumTarget } from '../../utils/lookupEnumTarget';
import {
  resolveEffectiveLookupDeclaration,
  resolveLookupPairsMode,
} from '../../utils/effectiveLookupDeclaration';

export type EnumPickerSlotContext = {
  connectionId: string | undefined;
  database: string | undefined;
  schema: string | undefined;
  table: string | undefined;
  engine: DatabaseEngineId | undefined;
  column: TableExplorerColumn | undefined;
  metaInfo: DatabaseMetaInfo | null | undefined;
};

export type EnumPickerSlotResult =
  | {
      /** The `enum` filter operator is offered for this column. */
      available: true;
      /** Common picker context (column identity + connection). Spread straight
       *  into `EnumValuePicker` — every field is one of its props. */
      context: {
        connectionId: string;
        database: string;
        schema: string;
        table: string;
        column: string;
        engine: DatabaseEngineId;
        /**
         * The single unconditional FK target this column resolves to, when it
         * has one — drives the picker's "Search rows in …" escape into the
         * full row picker. Absent for non-FK / polymorphic / array columns.
         */
        fkBrowseTarget?: EnumLookupTarget;
        /** The winning loose FK's `targetFilter`, so the row picker stays scoped. */
        fkBrowseTargetFilter?: FilterGroup[];
        /** Source column's data type — the row picker's source context. */
        sourceDataType?: string;
      };
    }
  | { available: false };

/**
 * Resolve whether the filter chip can render an `EnumValuePicker` for
 * `column` — i.e. the column carries the `enum` operator (declared, native,
 * or eligible). Returns `{ available: false }` whenever any piece of context
 * required by the picker is missing or the column is `not-enum`.
 *
 * Unlike the previous design, this no longer hijacks `=`/`!=`: the chip
 * renders the picker only when `condition.operator === 'enum'`. `=`/`!=`
 * stay free-text.
 */
export function useEnumPickerSlot(
  ctx: EnumPickerSlotContext,
): EnumPickerSlotResult {
  const { connectionId, database, schema, table, engine, column, metaInfo } =
    ctx;
  return useMemo<EnumPickerSlotResult>(() => {
    // `schema == null` (not `!schema`) so that '' is accepted as the canonical
    // empty-schema value for SQLite, MySQL, and schemaless Kusto — `!''` would
    // incorrectly block them. SQL engines always pass a non-empty schema, so
    // only a genuinely absent (undefined/null) schema is missing context.
    if (!connectionId || !database || schema == null || !table || !engine || !column) {
      return { available: false };
    }
    // Same native-enum signal as `getOperatorsForColumnWithMetaInfo` — without
    // it an undeclared PG enum got the `enum` operator but no picker.
    const input: EnumColumnInput = {
      engine,
      schema,
      table,
      name: column.name,
      dataType: column.dataType,
      ...(column.isNativeEnumType ? { isNativeEnumType: true } : {}),
    };
    // Browse-all-rows escape: the same eligibility rule the declare dialog
    // uses. `column.isLoose` marks an FK that came from a loose declaration,
    // not a real constraint — those belong in `looseRels`, never in `realFk`.
    // Resolved BEFORE the placement gate below, because an implicit lookup
    // enum is defined by this target.
    const resolution = resolveLookupEnumTarget({
      dataType: column.dataType,
      realFk:
        column.referencedTable && column.referencedColumn && !column.isLoose
          ? {
              ...(column.referencedSchema != null
                ? { referencedSchema: column.referencedSchema }
                : {}),
              referencedTable: column.referencedTable,
              referencedColumn: column.referencedColumn,
            }
          : undefined,
      looseRels: findLooseRelationshipsForColumn(
        metaInfo?.looseRelationships,
        schema,
        table,
        column.name,
      ),
    });
    // An IMPLICIT lookup enum (no declaration, but the FK target's table is
    // flagged in `referenceTables`) offers the enum operator whatever its type
    // says — the values ARE a short, knowable list, they just live in another
    // table. This is the one operator-placement call site that already has the
    // resolved target in hand; `getOperatorsForColumnWithMetaInfo` has no FK
    // context, so its ordering for implicit columns is unchanged (see
    // `Docs/superpowers/specs/2026-08-29-reference-table-lookup-design.md`).
    //
    // …but only when its pairs are obtained in `'cached'` mode. That "short,
    // knowable list" is exactly what an `'on-demand'` target does NOT have —
    // offering the picker there would open a dropdown that can never be
    // populated. Those columns keep the plain operators and the FK row picker.
    const effective = resolveEffectiveLookupDeclaration({
      metaInfo,
      schema,
      table,
      column: column.name,
      resolvedTarget: resolution.target,
    });
    const pairsMode = effective ? resolveLookupPairsMode(metaInfo, effective) : 'cached';
    // …and an EXPLICIT declaration cannot buy the picker back. The declaration
    // says what the column IS; the target table says how its pairs are
    // obtained, and an on-demand target has none to list. Offering the picker
    // here would open an unfillable dropdown AND persist a ≤201-pair cache for
    // a target the user flagged never-persist (B-1).
    if (pairsMode === 'on-demand') return { available: false };
    const isImplicitLookup = effective?.kind === 'implicit';
    if (
      !isImplicitLookup &&
      inferEnumOperatorPlacement(input, metaInfo ?? null) === 'absent'
    ) {
      return { available: false };
    }
    return {
      available: true,
      context: {
        connectionId,
        database,
        schema,
        table,
        column: column.name,
        engine,
        ...(resolution.target ? { fkBrowseTarget: resolution.target } : {}),
        ...(resolution.target && resolution.targetFilter
          ? { fkBrowseTargetFilter: resolution.targetFilter }
          : {}),
        ...(column.dataType ? { sourceDataType: column.dataType } : {}),
      },
    };
  }, [connectionId, database, schema, table, engine, column, metaInfo]);
}
